/* eslint-disable camelcase */
import type { SearchQuery } from '@algolia/client-search';

import { useEffect, useMemo, useState } from 'react';

import type { AlgoliaProductHit } from '~/types/algolia';
import type {
  ExponeaAssociationsSlots,
  ExponeaPersonalizationSlots,
  ExponeaSlotOptionalFilters,
} from '~/types/exponea';
import { PageTypes } from '~/utilities/constants';
import { ATTRIBUTES_TO_RETRIEVE } from '~/utilities/constants/algolia';
import { EXPONEA_PRODUCT_INTEREST_DATA } from '~/utilities/constants/localStorageKeys';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { uniqBy } from '~/utilities/helpers/uniqBy';
import { useAlgoliaABTestConfig } from '~/utilities/hooks/search/useAlgoliaABTestConfig';
import { useAlgoliaAttributesToRetrieve } from '~/utilities/hooks/search/useAlgoliaAttributesToRetrieve';
import { useAlgoliaClient } from '~/utilities/hooks/search/useAlgoliaClient';
import { useAlgoliaFilters } from '~/utilities/hooks/search/useAlgoliaFilters';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';
import logger from '~/utilities/logger';

const ExponeaToAlgoliaAttributesMap: Record<string, string> = {
  true_color: 'exactColorDescription',
};

export const useManagedProductHits = () => {
  const { country, pageType } = useAppContext();
  const { primaryIndex } = useAlgoliaSortingOptions();
  const [managedHits, setManagedHits] = useState<Array<AlgoliaProductHit>>([]);
  const [productInterestData] = useState<ExponeaPersonalizationSlots>(
    JSON.parse(sessionStorage.getItem(EXPONEA_PRODUCT_INTEREST_DATA) || '[]')
  );
  const filters = useAlgoliaFilters();
  const algoliaClient = useAlgoliaClient();
  const {
    categoryDetails: { ruleBasedPersonalizationMaterialGroup, categoryCode } = {},
    visibleFacetsList,
  } = useProductListContext();
  const { configuration: { ruleBasedPersonalizationAmountOfSlots } = {} } = useStaticContext();
  const attributesToRetrieve = useAlgoliaAttributesToRetrieve(ATTRIBUTES_TO_RETRIEVE);
  const { enableABTest, analyticsTags } = useAlgoliaABTestConfig();

  /**
   * RBP - Rule-Based Personalization, for category pages only.
   * SRP also carries `/shop/all` category details, but must never get personalized slots.
   */
  const RBPKey = `${country}_rbp`;
  const personalizedDataKey =
    pageType === PageTypes.CATEGORY && ruleBasedPersonalizationMaterialGroup
      ? `${categoryCode}|${ruleBasedPersonalizationMaterialGroup}`
      : undefined;

  const personalizedSlotFilters: Array<SearchQuery | undefined> = useMemo(() => {
    const slotMaterialGroupData = productInterestData?.products?.find(
      ({ materialGroup }) => ruleBasedPersonalizationMaterialGroup === materialGroup
    );

    const associations = (slotMaterialGroupData?.associations || {}) as ExponeaAssociationsSlots;
    const managedSlotData = Array.from(
      Object.values(associations) as Array<ExponeaSlotOptionalFilters | null>
    );

    return managedSlotData
      .filter(slotParam => !!slotParam)
      .map(slotParams => {
        const { styleCode, ...restOptionalFiltersData } = slotParams;
        const optionalFilters = Object.entries(restOptionalFiltersData)
          .map(([key, { value, algolia_score }]) => {
            const algoliaAttribute = ExponeaToAlgoliaAttributesMap[key];
            const attribute = visibleFacetsList.get(algoliaAttribute)?.localized
              ? `${algoliaAttribute}.EN`
              : algoliaAttribute;

            return algolia_score !== null
              ? `(${attribute}:${value}<score=${algolia_score}>)`
              : undefined;
          })
          .filter(Boolean);

        if (!primaryIndex) {
          return undefined;
        }

        if (!optionalFilters) {
          return undefined;
        }

        return {
          type: 'default',
          indexName: primaryIndex,
          params: {
            clickAnalytics: true,
            analytics: true,
            hitsPerPage: 1,
            filters: `${filters} AND styleCode:${styleCode.value}`,
            attributesToRetrieve,
            responseFields: ['hits'],
            attributesToHighlight: [],
            optionalFilters: optionalFilters.join(' AND '),
            enableABTest,
            analyticsTags,
          },
        };
      });
  }, [
    visibleFacetsList,
    filters,
    ruleBasedPersonalizationMaterialGroup,
    primaryIndex,
    productInterestData,
    attributesToRetrieve,
    enableABTest,
    analyticsTags,
  ]);

  useEffect(() => {
    if (!personalizedDataKey) {
      setManagedHits([]);

      return;
    }

    (async () => {
      try {
        const currentCachedData = JSON.parse(sessionStorage.getItem(RBPKey) || '{}');
        const currentCategoryHits = currentCachedData[personalizedDataKey] as
          | Array<AlgoliaProductHit>
          | undefined;

        // skip data fetch if data is present in sessionStorage
        if (currentCategoryHits?.length) {
          setManagedHits(currentCategoryHits);

          return;
        }

        const queries: SearchQuery[] = personalizedSlotFilters
          .slice(0, ruleBasedPersonalizationAmountOfSlots ?? 0)
          .filter(filter => !!filter);

        if (algoliaClient && queries.length) {
          const data = await algoliaClient.searchForHits<AlgoliaProductHit>(
            { requests: queries },
            { cacheable: true }
          );

          const hits = data.results.map((result, index) => ({
            ...result.hits.at(0),
            // queryID will be available as much as `clickAnalytics` is set to true
            __queryID: result.queryID,
            __position: index + 1,
            isRBP: true,
          }));

          const uniqueHits = uniqBy(hits, 'objectID') as Array<AlgoliaProductHit>;

          setManagedHits(uniqueHits);

          setTimeout(() => {
            sessionStorage.setItem(
              RBPKey,
              JSON.stringify({ ...currentCachedData, [personalizedDataKey]: uniqueHits })
            );
          });
        }
      } catch (e) {
        logger.error(e);
      }
    })();
  }, [
    algoliaClient,
    personalizedSlotFilters,
    ruleBasedPersonalizationAmountOfSlots,
    personalizedDataKey,
    RBPKey,
  ]);

  return managedHits;
};
